import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import { recipeCardsSwitchedOn } from '@/lib/recipeCard/recipeScale'

export const RECIPE_CARD_QK = 'recipe-cards'

/**
 * useRecipeCardsEnabled tells a kitchen screen whether to offer recipes: the
 * deploy runs the kitchen flow (`kitchen_flow`, env-authoritative) AND the
 * tenant's admin has switched recipe cards on (`kitchen_flow.recipe_cards`).
 *
 * A missing key, a failed fetch or anything unexpected reads as off — the safe
 * direction: a tenant that has not asked for this sees no button and makes no
 * new request. Shares the `['settings','all']` cache, so it adds no call.
 */
export function useRecipeCardsEnabled(): boolean {
  return recipeCardsSwitchedOn(useAllSettings())
}

export interface RecipeCardLookup {
  enabled: boolean
  /** The dish has ingredients or a method to show. */
  hasDish: (productId: string | null | undefined) => boolean
  hasPrep: (stockItemId: string | null | undefined) => boolean
}

/**
 * Which dishes and prep items have a card, so the recipe button appears only
 * where it leads somewhere. One small list, refreshed every few minutes; the
 * Kitchen Display's own order payload is not touched by this feature.
 */
export function useRecipeCardLookup(): RecipeCardLookup {
  const enabled = useRecipeCardsEnabled()
  const { data } = useQuery({
    queryKey: [RECIPE_CARD_QK, 'index'],
    queryFn: () => apiClient.getRecipeCardIndex().then((r) => r.data),
    enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  })
  const dishes = useMemo(() => new Set(data?.product_ids ?? []), [data])
  const preps = useMemo(() => new Set(data?.prep_item_ids ?? []), [data])
  const hasDish = useCallback((id: string | null | undefined) => enabled && !!id && dishes.has(id), [enabled, dishes])
  const hasPrep = useCallback((id: string | null | undefined) => enabled && !!id && preps.has(id), [enabled, preps])
  return { enabled, hasDish, hasPrep }
}
